import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScreeningDetailsDrawerComponent } from '@/app/pages/screening-brief-report/molecules/screening-details-drawer/screening-details-drawer';
import { OverrideDecisionDrawer } from '../override-decision-drawer/override-decision-drawer';
import { FlaggedCohortContent } from '../../data/screening-results.model';
import { BrokenRule, brokenRuleCaption, brokenRuleFindings, brokenRuleTypeLabels } from '../../services/screening-rule-detail/broken-rules';
import { ScreeningResultsService } from '../../services/screening-results/screening-results.service';
import { ScreeningRuleDetailService } from '../../services/screening-rule-detail/screening-rule-detail.service';
import {
  flaggedDecisionLabel,
  flaggedIssueLabel,
  flaggedMediaIsVideo,
  flaggedMediaUrl,
  flaggedPositionLabel,
  flaggedWatchLabel,
} from './flagged-content-card.helpers';

/**
 * One flagged post on the Content tab.
 *
 * Name, handle, thumbnail, content position, and issue count come from the cohort. The broken
 * rule loads once the card is on screen. Override lowers the flag. Reject stays a placeholder.
 */
@Component({
  selector: 'scr-flagged-content-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScreeningDetailsDrawerComponent, OverrideDecisionDrawer],
  templateUrl: './flagged-content-card.html',
  host: { class: 'block' },
})
export class FlaggedContentCard {
  private readonly ruleDetail = inject(ScreeningRuleDetailService);
  private readonly screening = inject(ScreeningResultsService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  readonly post = input.required<FlaggedCohortContent>();
  readonly briefId = input.required<string>();
  private readonly detailsDrawer = viewChild<ScreeningDetailsDrawerComponent>('detailsDrawer');
  private readonly overrideDrawer = viewChild<OverrideDecisionDrawer>('overrideDrawer');

  readonly ruleState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  readonly failedRules = signal<readonly BrokenRule[]>([]);

  readonly mediaUrl = computed(() => flaggedMediaUrl(this.post()));

  readonly hasMedia = computed(() => !!this.mediaUrl());

  readonly mediaIsVideo = computed(() => flaggedMediaIsVideo(this.mediaUrl()));

  readonly positionLabel = computed(() => flaggedPositionLabel(this.post()));

  readonly issueLabel = computed(() => flaggedIssueLabel(this.post().item.issues));

  readonly findings = computed(() => brokenRuleFindings(this.failedRules()));

  readonly ruleTypeLabels = computed(() =>
    brokenRuleTypeLabels(this.failedRules(), [
      ...this.screening.rules(),
      ...this.screening.brandSafetyRules(),
    ]),
  );

  readonly mediaAlt = computed(() => `${this.post().creatorName} content`);

  readonly watchLabel = computed(() => flaggedWatchLabel(this.post().creatorName));

  readonly rejectLabel = computed(() => flaggedDecisionLabel('Reject', this.post().creatorName));

  readonly overrideLabel = computed(() => flaggedDecisionLabel('Override', this.post().creatorName));

  constructor() {
    afterNextRender(() => {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        observer.disconnect();
        this.loadRules();
      });
      observer.observe(this.host.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  ruleCaption(type: BrokenRule['type']): string {
    return brokenRuleCaption(type);
  }

  /** Opens the screening details drawer for this post. */
  watch(): void {
    this.detailsDrawer()?.open();
  }

  /** Opens the note drawer that lowers this flag. */
  override(): void {
    this.overrideDrawer()?.open();
  }

  onOverridden(contentId: string): void {
    this.screening.markContentOverridden(contentId);
  }

  private loadRules(): void {
    const briefId = this.briefId();
    const contentId = this.post().item.id;
    if (!briefId || !contentId) {
      this.ruleState.set('error');
      return;
    }
    this.ruleState.set('loading');
    this.ruleDetail
      .rulesFor(contentId, briefId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rules) => {
          this.failedRules.set(rules);
          this.ruleState.set(rules.length ? 'ready' : 'error');
        },
        error: () => this.ruleState.set('error'),
      });
  }
}
